import { DestroyRef, Injectable, Signal } from '@angular/core';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  merge,
  Observable,
  ReplaySubject,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { isNotNull } from '@libs/utils';
import { PerioMeasurementSurface, TargetIndexMovement } from '../../components/perio-config/perio-config.vm';

export type PerioInputMode = 'pocketDepth' | 'recession';

@Injectable({ providedIn: 'root' })
export class PerioFormControlService {
  private readonly measurementOrder: PerioMeasurementSurface[] = ['distal', 'central', 'mesial'];
  private readonly activeSurface$ = new BehaviorSubject<PerioMeasurementSurface | null>(null);
  private readonly activeCursor$ = new BehaviorSubject<PerioMeasurementSurface | null>(null);
  private readonly activePocketDepthCursor$ = new BehaviorSubject<PerioMeasurementSurface | null>(null);
  private readonly lastActivePocketDepthCursor = toSignal(this.activePocketDepthCursor$.pipe(filter(Boolean)));
  private readonly nextPocketDepthDigit$ = new BehaviorSubject<number | null>(null);
  private readonly recessionTrigger$ = new Subject<void>();
  private readonly bleedingTrigger$ = new Subject<PerioMeasurementSurface>();
  private readonly suppurationTrigger$ = new Subject<PerioMeasurementSurface>();
  private readonly activeRecessionCursor$ = new BehaviorSubject<PerioMeasurementSurface | null>(null);
  private readonly mobilityTrigger$ = new Subject<void>();
  private readonly mobilityValue$ = new Subject<number>();
  private readonly furcuationTrigger$ = new Subject<void>();
  private readonly furcuationValue$ = new Subject<number>();
  private readonly recessionValue$ = new Subject<number>();
  private readonly movedWithoutValue$ = new Subject<void>();
  private readonly lastDirecton$ = new ReplaySubject<TargetIndexMovement>(1);
  private readonly triggerReset$ = new Subject<void>();
  private readonly inputMode$ = new BehaviorSubject<PerioInputMode>('pocketDepth');

  private readonly filterActiveRecessionSurface$ = (surface: PerioMeasurementSurface | null): Observable<PerioMeasurementSurface | null> =>
    this.getActiveRecessionCursor$().pipe(filter((side) => side === surface));

  public readonly focusedField$ = merge(
    this.activeRecessionCursor$.pipe(
      filter(Boolean),
      map(() => 'rec' as const),
    ),
    this.nextPocketDepthDigit$.pipe(map(() => 'pd' as const)),
  ).pipe(distinctUntilChanged());

  public readonly reset$ = this.triggerReset$.asObservable();

  public getPocketDepthTrigger$ = (
    surface: PerioMeasurementSurface,
    destroyRef: DestroyRef,
    readonly: Signal<boolean>,
  ): Observable<number | null> =>
    this.filterActiveSurface$(surface).pipe(
      filter(() => !readonly()),
      switchMap(() => this.nextPocketDepthDigit$.pipe(first())),
      takeUntilDestroyed(destroyRef),
    );

  public getRecessionTrigger$ = (surface: PerioMeasurementSurface, destroyRef: DestroyRef, readonly: Signal<boolean>): Observable<void> =>
    this.filterActiveRecessionSurface$(surface).pipe(
      filter(() => !readonly()),
      map(() => {}),
      takeUntilDestroyed(destroyRef),
    );

  public getBleedingTrigger$ = (surface: PerioMeasurementSurface, destroyRef: DestroyRef, readonly: Signal<boolean>): Observable<boolean> =>
    this.bleedingTrigger$.pipe(
      filter((triggerSurface) => triggerSurface === surface),
      filter(() => !readonly()),
      map(() => true),
      takeUntilDestroyed(destroyRef),
    );

  public getSuppurationTrigger$ = (
    surface: PerioMeasurementSurface,
    destroyRef: DestroyRef,
    readonly: Signal<boolean>,
  ): Observable<boolean> =>
    this.suppurationTrigger$.pipe(
      filter((triggerSurface) => triggerSurface === surface),
      filter(() => !readonly()),
      map(() => true),
      takeUntilDestroyed(destroyRef),
    );

  public getToothDoneTrigger$ = (destroyRef: DestroyRef, readonly: Signal<boolean>): Observable<PerioMeasurementSurface | null> =>
    this.filterActiveSurface$(null).pipe(
      filter(() => !readonly()),
      switchMap(() => {
        return this.getActivePocketDepthCursor$().pipe(debounceTime(0), first());
      }),
      filter((activePocketDepthCursor) => !isNotNull(activePocketDepthCursor)),
      map(() => this.activeSurface$.value),
      takeUntilDestroyed(destroyRef),
    );

  public getMovedWithoutValue$(destroyRef: DestroyRef): Observable<void> {
    return this.movedWithoutValue$.asObservable().pipe(takeUntilDestroyed(destroyRef));
  }

  public getFocusedField$(): Observable<'rec' | 'pd'> {
    return this.focusedField$.pipe(startWith('pd' as const));
  }

  public getLastDigit(): number {
    return this.nextPocketDepthDigit$.value as number;
  }

  public reset(): void {
    this.activeCursor$.next(null);
    this.activeSurface$.next(null);
    this.activePocketDepthCursor$.next(null);
    this.activeRecessionCursor$.next(null);
    this.lastDirecton$.next(TargetIndexMovement.None);
    this.nextPocketDepthDigit$.next(null);
    this.triggerReset$.next();
  }

  public focusPocketDepth(
    surface: PerioMeasurementSurface,
    reversed = false,
    cursorMovementDirection: TargetIndexMovement,
    digit?: number,
    newTooth?: boolean,
    skipToothDoneCheck?: boolean,
  ): void {
    const measurementOrder = this.getMeasurementOrder(reversed);
    const targetIndex = measurementOrder.indexOf(surface) + cursorMovementDirection;
    const target = skipToothDoneCheck
      ? this.activeSurface$.value
      : targetIndex >= 0 && targetIndex < measurementOrder.length
        ? measurementOrder[targetIndex] || null
        : null;

    this.nextPocketDepthDigit$.next(digit === undefined ? null : digit);
    this.lastDirecton$.next(cursorMovementDirection);
    const previousActiveSurface = this.activeSurface$.value;

    this.activeSurface$.next(
      isNotNull(target) || this.activePocketDepthCursor$.value !== measurementOrder[1] ? target : previousActiveSurface,
    );

    const activePdTargetIndex = this.getActivePocketDepthTargetIndex(
      targetIndex,
      cursorMovementDirection,
      previousActiveSurface!,
      measurementOrder,
      digit,
      newTooth,
    );

    const activePdTarget = isNotNull(activePdTargetIndex) ? measurementOrder[activePdTargetIndex] : null;
    this.setActivePocketDepthCursor(activePdTarget);
  }

  public focusActivePocketDepth(reversed = false): void {
    const measurementOrder = this.getMeasurementOrder(reversed);
    const isLastMeasurement = this.activeSurface$.value === measurementOrder.at(-1);
    const cursorMovementDirection = isLastMeasurement ? TargetIndexMovement.Next : TargetIndexMovement.None;

    this.focusPocketDepth(this.activeSurface$.value!, reversed, cursorMovementDirection, undefined, false, isLastMeasurement);
  }

  public focusMobility(): void {
    this.mobilityTrigger$.next();
  }

  public setMobilityValue(value: number): void {
    this.mobilityValue$.next(value);
  }

  public setMovedWithoutValue(): void {
    this.movedWithoutValue$.next();
  }

  public focusFurcuation(): void {
    this.furcuationTrigger$.next();
  }

  public setFurcationValue(value: number): void {
    this.furcuationValue$.next(value);
  }

  public setRecessionValue(value: number): void {
    this.recessionValue$.next(value);
  }

  public focusRecessionOfActiveSurface(): void {
    // Set active surface to match current active cursor so recession on same surface gets focused
    const activeCursorSurface = this.activeCursor$.value;
    if (activeCursorSurface) {
      this.activeSurface$.next(activeCursorSurface);
      this.setActiveCursor(activeCursorSurface); // Set active cursor for visual feedback
    }
    this.recessionTrigger$.next();
  }

  public triggerBleedingOnSurface(surface: PerioMeasurementSurface): void {
    this.bleedingTrigger$.next(surface);
  }

  public triggerSuppurationOnSurface(surface: PerioMeasurementSurface): void {
    this.suppurationTrigger$.next(surface);
  }

  public removePreservedDigit(): void {
    this.nextPocketDepthDigit$.next(null);
  }

  public getActiveCursorValue(): PerioMeasurementSurface | null {
    return this.activePocketDepthCursor$.value;
  }

  public getActiveCursor$(): Observable<PerioMeasurementSurface | null> {
    return this.activeCursor$.asObservable();
  }

  public getLastCursorMovementDirection$(): Observable<TargetIndexMovement> {
    return this.lastDirecton$.pipe(first());
  }

  public getMobilityTrigger$(): Observable<void> {
    return this.mobilityTrigger$.asObservable();
  }

  public getMobilityValue$(): Observable<number> {
    return this.mobilityValue$.asObservable();
  }

  public getFurcuationTrigger$(): Observable<void> {
    return this.furcuationTrigger$.asObservable();
  }

  public getFurcuationValue$(): Observable<number> {
    return this.furcuationValue$.asObservable();
  }

  public getRecessionValue$(): Observable<number> {
    return this.recessionValue$.asObservable();
  }

  public getMeasurementOrder(reversed: boolean): PerioMeasurementSurface[] {
    return reversed ? [...this.measurementOrder].reverse() : [...this.measurementOrder];
  }

  public setActivePocketDepthCursor(surface: PerioMeasurementSurface | null, next: boolean = false, reversed: boolean = false): void {
    if (surface === null) {
      queueMicrotask(() => {
        this.activePocketDepthCursor$.next(null);
      });
      return;
    }
    const measurementOrder = this.getMeasurementOrder(reversed);
    const index = measurementOrder.indexOf(surface) + (next ? 1 : 0);

    const activeCursor = index < measurementOrder.length ? measurementOrder[index] : null;

    queueMicrotask(() => {
      this.activePocketDepthCursor$.next(activeCursor);
    });
  }

  public setActiveCursor(surface: PerioMeasurementSurface | null): void {
    queueMicrotask(() => {
      this.activeCursor$.next(surface);
    });
  }

  public getActivePocketDepthCursor$(): Observable<PerioMeasurementSurface | null> {
    return this.activePocketDepthCursor$.asObservable();
  }

  public getActivePocketDepthCursor(): PerioMeasurementSurface | null {
    return this.activePocketDepthCursor$.value;
  }

  public getLastActivePocketDepthCursor(): PerioMeasurementSurface | null {
    return this.lastActivePocketDepthCursor() as PerioMeasurementSurface | null;
  }

  public getActiveCursor(): PerioMeasurementSurface | null {
    return this.activeCursor$.value;
  }

  public refocusLastActivePocketDepth(reversed = false): void {
    const lastCursor = this.lastActivePocketDepthCursor() || this.activePocketDepthCursor$.value;
    if (lastCursor) {
      this.focusPocketDepth(lastCursor, reversed, TargetIndexMovement.None);
    }
  }
  public getActiveRecessionCursor$(): Observable<PerioMeasurementSurface | null> {
    return this.activeRecessionCursor$.asObservable();
  }

  public getActiveRecessionCursor(): PerioMeasurementSurface | null {
    return this.activeRecessionCursor$.value;
  }

  public setActiveRecessionCursor(surface: PerioMeasurementSurface | null): void {
    queueMicrotask(() => {
      this.activeRecessionCursor$.next(surface);
    });
  }

  public focusRecession(
    surface: PerioMeasurementSurface,
    reversed = false,
    cursorMovementDirection: TargetIndexMovement,
    isCentralRecession = false,
  ): void {
    if (isCentralRecession && cursorMovementDirection === TargetIndexMovement.None) {
      this.setActiveRecessionCursor('central');
      this.activeSurface$.next('central');
      return;
    }

    const measurementOrder = this.getMeasurementOrder(reversed);
    const targetIndex = measurementOrder.indexOf(surface) + cursorMovementDirection;
    const target = targetIndex < measurementOrder.length && targetIndex >= 0 ? measurementOrder[targetIndex] : null;

    this.lastDirecton$.next(cursorMovementDirection);
    this.setActiveRecessionCursor(target);
  }

  public setInputMode(mode: PerioInputMode): void {
    this.inputMode$.next(mode);
  }

  public getInputMode(): PerioInputMode {
    return this.inputMode$.value;
  }

  public getInputMode$(): Observable<PerioInputMode> {
    return this.inputMode$.asObservable();
  }

  private getActivePocketDepthTargetIndex(
    targetIndex: number | null,
    cursorMovementDirection: TargetIndexMovement,
    previousActiveSurface: PerioMeasurementSurface,
    measurementOrder: PerioMeasurementSurface[],
    digit?: number,
    newTooth?: boolean,
  ): number | undefined {
    if (!isNotNull(targetIndex)) {
      return;
    }
    let activePdTargetIndex;
    if (newTooth) {
      const moveTargetBy = isNotNull(digit) ? 1 : 0;
      activePdTargetIndex = targetIndex + moveTargetBy;
    } else if (cursorMovementDirection === TargetIndexMovement.Next) {
      activePdTargetIndex = this.activePocketDepthCursor$.value === this.activeSurface$.value ? targetIndex + 1 : targetIndex;
    } else if (cursorMovementDirection === TargetIndexMovement.None) {
      const currentPdCursor = this.getActivePocketDepthCursor()! || this.lastActivePocketDepthCursor()!;
      activePdTargetIndex = measurementOrder.indexOf(currentPdCursor);
    } else {
      activePdTargetIndex = this.activePocketDepthCursor$.value === previousActiveSurface ? targetIndex : targetIndex + 1;
    }

    return activePdTargetIndex;
  }

  private readonly filterActiveSurface$ = (surface: PerioMeasurementSurface | null): Observable<PerioMeasurementSurface | null> =>
    this.activeSurface$.asObservable().pipe(filter((side) => side === surface));
}
